/* eslint-disable react/prop-types */
import React, { Suspense, useState } from "react";
import { Layout } from "antd";
import { CORELoading } from "../../../COREDesignSystem/Feedback/CORELoading";
import "./PageLayout.less";
import { ErrorBoundaryWithRouter as ErrorBoundary } from "../../../shared/errors/ErrorBoundary";
import { generateTestId } from "../../../shared/testids/testids";
import { COREBackToTop } from "../../../COREDesignSystem/Navigation/COREBackToTop";
import classNames from "classnames";
import { CORETopHeader } from "../../../COREDesignSystem/Navigation/CORETopHeader";
import { useIsMobileScreen } from "../../../shared/customHoooks/useBreakpoint";
import { COREMainMenuBlock } from "../../../COREDesignSystem/Navigation/COREMainMenuBlock";
import { useLocalStorageUntilLogout } from "../../../shared/customHoooks/useLocalStorageUntilLogout";
import { BreadcrumbItem } from "../../../COREDesignSystem/Navigation/COREBreadcrumb";
import { ReleaseNoteModal } from "./ReleaseNoteModal";
import { useUserRole } from "../../../shared/state/user";
import { useMinimalMode } from "../../../shared/state/minimalModeContext";
import { FullScreenModeContext } from "../../../shared/state/fullScreenModeContext";
import { MainMenuProvider } from "../../../COREDesignSystem/Navigation/MainMenuContext";
import {
  UserPreference,
  useUserConfigs,
} from "../../../shared/customHoooks/useUserConfig";

export const PageLayout: React.FC<{
  blur?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  children?: React.ReactNode;
}> = React.memo(({ blur, children, breadcrumbs }) => {
  const isPendingRole = useUserRole() === "pending";
  const [isMenuHidden, setIsMenuHidden] = useState(true);
  const { configs, loading, sync } =
    useUserConfigs<UserPreference>("preference");
  const menuPosition = configs?.[0]?.data?.config.menuPosition ?? "expanded";
  const [isPanel, setIsPanel] = useLocalStorageUntilLogout<boolean>({
    key: "mainMenuIsPanel",
    initialValue: menuPosition === "expanded",
  });
  const [isFullScreenMode, setIsFullScreenMode] = useState<boolean>(false);
  const { minimalMode } = useMinimalMode();

  const isMobileScreen = useIsMobileScreen();
  const toggleMobileMenu = () => {
    setIsMenuHidden(!isMenuHidden);
  };
  return (
    <>
      <FullScreenModeContext.Provider
        value={{ isFullScreenMode, setIsFullScreenMode }}
      >
        <MainMenuProvider isPanel={isPanel} menuPosition={menuPosition}>
          <div
            className={classNames("page-layout-block", {
              "main-layout-content-blur": blur,
            })}
          >
            <Layout>
              {!(isFullScreenMode || minimalMode) && (
                <Layout.Header className={"top-header-sticky"}>
                  <Suspense
                    fallback={<CORELoading delay={0} layout="horizontal" />}
                  >
                    <CORETopHeader
                      toggleMobileMenu={toggleMobileMenu}
                      breadcrumbs={breadcrumbs}
                    />
                  </Suspense>
                </Layout.Header>
              )}
              <Layout
                className={classNames("ant-layout-has-sider", {
                  "mobile-menu-active": isMenuHidden && isMobileScreen,
                })}
              >
                {!(minimalMode || isPendingRole || (loading && !sync)) && (
                  <>
                    <Suspense fallback={<CORELoading delay={0} />}>
                      <ReleaseNoteModal />
                    </Suspense>
                    <COREMainMenuBlock
                      testID={generateTestId("page", "main-menu-block")}
                      isHidden={isMenuHidden && isMobileScreen}
                      setIsHidden={setIsMenuHidden}
                      isPanel={isPanel}
                      setIsPanel={setIsPanel}
                    />
                  </>
                )}
                <Layout className={"content-layout"}>
                  <Layout.Content className={"main-content-block navy-bg"}>
                    <Suspense fallback={<CORELoading />}>
                      <ErrorBoundary>
                        <div className={"main-layout-outer-block"}>
                          <div className={"main-layout-content-block"}>
                            {children}
                            <COREBackToTop
                              testID={generateTestId("page", `back-to-top`)}
                            />
                          </div>
                        </div>
                      </ErrorBoundary>
                    </Suspense>
                  </Layout.Content>
                </Layout>
              </Layout>
            </Layout>
          </div>
        </MainMenuProvider>
      </FullScreenModeContext.Provider>
    </>
  );
});
